import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SurfaceCard } from "@/components/common/Cards";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Logo, LogoPlaceholder } from "@/components/common/Logo";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, CheckCircle2, RefreshCw, Printer, Share2, Pencil, Sparkles, ShieldCheck,
  AlertTriangle, Check, Clock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell,
} from "recharts";
import { toast } from "sonner";

function Section({ title, children, id }) {
  return (
    <div className="mb-8" data-testid={`report-section-${id}`}>
      <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#00A85A] mb-3 pb-2 border-b border-[#E2E8F0]">{title}</h3>
      {children}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#F4FBF7] p-4">
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-[#101516] tabular-nums">{value || "—"}</div>
    </div>
  );
}

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/reports/${id}`).then((res) => setR(res.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  if (!r) return <Skeleton className="h-[80vh] rounded-2xl" />;
  const c = r.content || {};
  const site = c.implant_site || {};

  const approve = async () => { setBusy(true); try { await api.post(`/reports/${id}/approve`); toast.success("Report approved"); load(); } finally { setBusy(false); } };
  const regenerate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/ai/report/generate", { case_id: r.case_id, type: r.type }); toast.success("New draft generated"); navigate(`/reports/${data.id}`); }
    catch { toast.error("Could not regenerate"); } finally { setBusy(false); }
  };

  const boneData = [
    { name: "Bone Height", value: parseFloat(site.bone_height) || 0, fill: "#00A85A" },
    { name: "Bone Width", value: parseFloat(site.bone_width) || 0, fill: "#008F4D" },
    { name: "IAN Clearance", value: parseFloat(site.ian_clearance) || 0, fill: "#3FD98A" },
  ];

  const timeline = [
    { label: "Consultation", done: true }, { label: "Assessment", done: true },
    { label: "Diagnosis", done: true }, { label: "Planning", done: r.status === "approved" },
    { label: "Consent", done: false }, { label: "Surgery", done: false }, { label: "Follow-up", done: false },
  ];

  return (
    <div>
      {/* Toolbar (hidden on print) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5 print:hidden">
        <button onClick={() => navigate("/reports")} className="flex items-center gap-1.5 text-sm font-semibold text-[#687173] dark:text-[#8B9698] hover:text-[#101516] dark:hover:text-white"><ArrowLeft className="h-4 w-4" />Reports</button>
        <div className="flex flex-wrap items-center gap-2">
          {r.status !== "approved" ? (
            <>
              <Button variant="outline" onClick={regenerate} disabled={busy} data-testid="report-regenerate" className="rounded-lg border-[#E2E8F0] dark:border-[#223139]"><RefreshCw className="h-4 w-4 mr-1.5" />Regenerate</Button>
              <Button variant="outline" data-testid="report-edit" className="rounded-lg border-[#E2E8F0] dark:border-[#223139]"><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
              <Button onClick={approve} disabled={busy} data-testid="report-approve" className="bg-[#00A85A] hover:bg-[#008F4D] text-white rounded-lg"><CheckCircle2 className="h-4 w-4 mr-1.5" />Review & Approve</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => window.print()} data-testid="report-print" className="rounded-lg border-[#E2E8F0] dark:border-[#223139]"><Printer className="h-4 w-4 mr-1.5" />Print</Button>
              <Button variant="outline" onClick={() => toast.success("Share link copied")} className="rounded-lg border-[#E2E8F0] dark:border-[#223139]"><Share2 className="h-4 w-4 mr-1.5" />Share</Button>
            </>
          )}
        </div>
      </div>

      {/* AI review banner */}
      {r.status !== "approved" && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-4 print:hidden">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-500/20 shrink-0"><Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400" /></div>
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-800 dark:text-amber-300">AI-generated draft · Doctor review required</div>
            <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-0.5">This report was drafted by CONTAHA AI. Please review all clinical content before approving. AI assists, the clinician decides.</p>
          </div>
        </div>
      )}

      {/* Document */}
      <div className="mx-auto max-w-3xl">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_2px_20px_rgba(0,0,0,0.04)] overflow-hidden" data-testid="report-document">
          {/* Cover / header */}
          <div className="bg-[#07131A] contaha-grid p-7 text-white">
            <div className="flex items-start justify-between gap-4">
              <Logo size={30} />
              <LogoPlaceholder label="CLINIC LOGO" className="w-28 bg-white/5 border-white/15" />
            </div>
            <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#3FD98A]">{r.type}</div>
                <h1 className="mt-1 text-2xl font-extrabold">{c.patient_name || r.patient_name}</h1>
                <div className="mt-1 text-xs text-[#8B9698]">Prepared by {user?.name} · {user?.clinic_name}</div>
              </div>
              <div className="text-right text-xs text-[#8B9698] space-y-0.5">
                <div>Case: <span className="text-white font-semibold">{r.case_code || "—"}</span></div>
                <div>Date: <span className="text-white font-semibold">{formatDate(r.created_at)}</span></div>
                <div className="pt-1">{r.status === "approved" ? <span className="inline-flex items-center gap-1 rounded-md bg-[#00A85A]/20 px-2 py-0.5 text-[11px] font-semibold text-[#3FD98A]"><ShieldCheck className="h-3 w-3" />Doctor Approved</span> : <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/20 px-2 py-0.5 text-[11px] font-semibold text-amber-300"><Clock className="h-3 w-3" />Review Required</span>}</div>
              </div>
            </div>
          </div>

          <div className="p-7">
            {/* Executive summary */}
            <div className="mb-8 rounded-xl border border-[#00A85A]/20 bg-[#F4FBF7] p-5">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#00A85A] mb-2">Executive Clinical Summary</div>
              <p className="text-sm leading-relaxed text-[#101516]">{c.executive_summary || "—"}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Section title="Chief Complaint" id="complaint"><p className="text-sm text-[#687173] leading-relaxed">{c.chief_complaint || "—"}</p></Section>
              <Section title="Medical History" id="history">
                <ul className="space-y-1.5">{(c.medical_history || []).map((m, i) => <li key={i} className="flex gap-2 text-sm text-[#687173]"><Check className="h-4 w-4 text-[#00A85A] shrink-0 mt-0.5" />{m}</li>)}</ul>
              </Section>
            </div>

            <Section title="Clinical Findings" id="findings">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(c.clinical_findings || []).map((f, i) => (
                  <div key={i} className="rounded-xl border border-[#E2E8F0] p-4"><div className="text-xs font-bold text-[#101516]">{f.label}</div><div className="text-xs text-[#687173] mt-1">{f.detail}</div></div>
                ))}
              </div>
            </Section>

            <Section title="Diagnostic Summary" id="diagnosis"><p className="text-sm text-[#687173] leading-relaxed">{c.diagnostic_summary || "—"}</p></Section>

            <Section title={`Implant Site — Tooth ${site.tooth || "—"}`} id="implant-site">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Metric label="Bone Height" value={site.bone_height} />
                <Metric label="Bone Width" value={site.bone_width} />
                <Metric label="IAN Clearance" value={site.ian_clearance} />
                <Metric label="Bone Type" value={site.bone_type} />
              </div>
              <div className="rounded-xl border border-[#E2E8F0] p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#8B9698] mb-2">Bone Measurements (mm)</div>
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={boneData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11, fill: "#687173" }} axisLine={false} tickLine={false} />
                    <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>{boneData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title="Treatment Plan" id="treatment">
              <div className="space-y-2.5">
                {(c.treatment_plan || []).map((ph, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] p-3.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#E8F7F0] text-xs font-bold text-[#008F4D]">{i + 1}</div>
                    <div className="flex-1"><div className="text-sm font-semibold text-[#101516]">{ph.phase}</div><div className="text-xs text-[#687173]">{ph.description}</div></div>
                    <StatusBadge status={ph.status === "planned" ? "info" : ph.status} />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Clinical Timeline" id="timeline">
              <div className="flex items-center overflow-x-auto thin-scroll pb-2">
                {timeline.map((t, i) => (
                  <div key={i} className="flex items-center shrink-0">
                    <div className="flex flex-col items-center gap-1.5">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold ${t.done ? "bg-[#00A85A] text-white" : "border-2 border-[#E2E8F0] text-[#8B9698]"}`}>{t.done ? <Check className="h-3.5 w-3.5" /> : i + 1}</div>
                      <span className="text-[10px] font-semibold text-[#687173] whitespace-nowrap">{t.label}</span>
                    </div>
                    {i < timeline.length - 1 && <div className={`h-0.5 w-8 -mt-4 ${t.done ? "bg-[#00A85A]" : "bg-[#E2E8F0]"}`} />}
                  </div>
                ))}
              </div>
            </Section>

            {(c.recommendations || []).length > 0 && (
              <Section title="Recommendations" id="recommendations">
                <ul className="space-y-1.5">{c.recommendations.map((rec, i) => <li key={i} className="flex gap-2 text-sm text-[#687173]"><Check className="h-4 w-4 text-[#00A85A] shrink-0 mt-0.5" />{rec}</li>)}</ul>
              </Section>
            )}

            {(c.risk_flags || []).length > 0 && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 mb-8">
                <div className="flex items-center gap-2 mb-2"><AlertTriangle className="h-4 w-4 text-red-500" /><span className="text-xs font-bold uppercase tracking-wider text-red-600">Risk Flags</span></div>
                <ul className="space-y-1">{c.risk_flags.map((rf, i) => <li key={i} className="text-sm text-red-700">• {rf}</li>)}</ul>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-[#E2E8F0] pt-5 flex flex-wrap items-end justify-between gap-4">
              <div className="text-xs text-[#8B9698]"><div className="font-semibold text-[#101516]">{user?.clinic_name}</div><div>Generated by CONTAHA · {formatDate(r.created_at)}</div></div>
              <div className="text-right">
                {r.status === "approved" ? (
                  <><div className="text-[10px] font-bold uppercase tracking-wider text-[#00A85A]">Doctor Approved</div><div className="text-sm font-bold text-[#101516] mt-0.5 italic">{r.approved_by}</div><div className="border-t border-[#101516]/20 w-40 mt-1 pt-1 text-[10px] text-[#8B9698]">Signature</div></>
                ) : (
                  <div className="border-t border-[#8B9698]/40 w-40 pt-1 text-[10px] text-[#8B9698]">Doctor Signature (pending)</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
